import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowDownIcon,
  BadgeMinusIcon,
  BanknoteArrowUpIcon,
  CalendarCheckIcon,
  CircleDollarSignIcon,
  ClipboardXIcon,
  Clock2Icon,
  FileX2Icon,
  ListChecksIcon,
  ListFilterPlusIcon,
  MailCheckIcon,
  MessageSquareIcon,
  PhoneOutgoingIcon,
  RadioIcon,
  RefreshCwIcon,
  SendIcon,
  TrophyIcon,
  UserPenIcon,
  UserSearchIcon,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";

export default function TabsSection() {
  return (
    <section id="tabs-section">
      <div className="w-full md:px-9 flex justify-center">
        <div className="w-full max-w-[1291px] ">
          <Tabs defaultValue="leads">
            <div className="overflow-x-auto px-5 md:px-0 scrollbar-none">
              <TabsList className="w-full grid grid-cols-5 gap-3 min-w-[1150px]">
                <TabsTrigger value="leads">Triple Verified Leads</TabsTrigger>
                <TabsTrigger value="intent">Buying Intent Signals</TabsTrigger>
                <TabsTrigger value="list">
                  Fast Lead List Generation
                </TabsTrigger>
                <TabsTrigger value="crm">Seamless CRM Integration</TabsTrigger>
                <TabsTrigger value="cold">Cold Email Training</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent
              value="leads"
              className="w-full gap-[42px] md:gap-16 flex flex-col overflow-hidden bg-[#F8F9FA] border border-[#0D2A4C08] lg:px-28 pt-8 md:pt-12"
            >
              <div className="flex flex-col md:flex-row md:justify-between gap-8 px-5">
                <div className="flex flex-col gap-3.5 md:gap-5">
                  <p className="max-w-[287px] md:max-w-[548px] text-[32px] md:text-[56px] leading-[32px] md:leading-[58px] text-[#0D2A4C] tracking-tighter font-semibold">
                    Only Triple-Verified Contact Data
                  </p>
                  <div className="flex gap-2">
                    <Link
                      href="/pricing"
                      className="bg-[#2A8DFF] w-fit text-center hover:bg-[#2A8DFF]/90 active:outline-1 active:outline-[#2A8DFF] active:bg-[#2A8DFF] rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-white text-sm md:text-[16px] font-semibold">
                        Get Started Now
                      </p>
                    </Link>
                    <Link
                      href="/demo-form"
                      className="bg-white w-fit hover:bg-[#2A8DFF]/5 active:outline-1 active:!outline-white active:bg-white rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-[#0D2A4C] text-sm md:text-[16px] font-semibold">
                        Book a Demo
                      </p>
                    </Link>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex gap-1 items-center">
                    <ListChecksIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Verified for email, domain, and activity
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <BadgeMinusIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      No need for extra verification tools
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <ArrowDownIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Bounce rates under 1%
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <BanknoteArrowUpIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Only pay for qualified leads
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:block w-full h-[350px] relative">
                <Image
                  src="/images/leads-tab.webp"
                  alt="Leads Tab"
                  fill
                  className="object-cover object-top px-5"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
              <div className="block md:hidden">
                <Image
                  src="/images/leads-tab-mobile.webp"
                  alt="Leads Tab"
                  width={780}
                  height={478}
                  className="w-full"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
            </TabsContent>
            <TabsContent
              value="intent"
              className="w-full gap-[42px] md:gap-16 flex flex-col overflow-hidden bg-[#F8F9FA] border border-[#0D2A4C08] lg:px-28 pt-8 md:pt-12"
            >
              <div className="flex flex-col md:flex-row md:justify-between gap-8 px-5">
                <div className="flex flex-col gap-3.5 md:gap-5">
                  <p className="max-w-[277px] md:max-w-[548px] text-[32px] md:text-[56px] leading-[32px] md:leading-[58px] text-[#0D2A4C] tracking-tighter font-semibold">
                    Target Prospects in Buying Mode
                  </p>
                  <div className="flex gap-2">
                    <Link
                      href="/pricing"
                      className="bg-[#2A8DFF] w-fit text-center hover:bg-[#2A8DFF]/90 active:outline-1 active:outline-[#2A8DFF] active:bg-[#2A8DFF] rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-white text-sm md:text-[16px] font-semibold">
                        Get Started Now
                      </p>
                    </Link>
                    <Link
                      href="/demo-form"
                      className="bg-white w-fit hover:bg-[#2A8DFF]/5 active:outline-1 active:!outline-white active:bg-white rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-[#0D2A4C] text-sm md:text-[16px] font-semibold">
                        Book a Demo
                      </p>
                    </Link>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex gap-1 items-center">
                    <RadioIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      21B+ buying intent signals
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <ListFilterPlusIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Filter by 21,000+ tracked topics
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <CalendarCheckIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Intent signals updated daily
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <UserSearchIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Leads already researching your offer
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:block w-full px-5">
                <Image
                  src="/images/intent-tab.webp"
                  alt="Intent Tab"
                  width={2140}
                  height={742}
                  className="w-full"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
              <div className="block md:hidden">
                <Image
                  src="/images/intent-tab-mobile.webp"
                  alt="Intent Tab"
                  width={780}
                  height={626}
                  className="w-full"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
            </TabsContent>
            <TabsContent
              value="list"
              className="w-full gap-[42px] md:gap-16 flex flex-col overflow-hidden bg-[#F8F9FA] border border-[#0D2A4C08] lg:px-28 pt-8 md:pt-12"
            >
              <div className="flex flex-col md:flex-row md:justify-between gap-8 px-5">
                <div className="flex flex-col gap-3.5 md:gap-5">
                  <p className="max-w-[297px] md:max-w-[548px] text-[32px] md:text-[56px] leading-[32px] md:leading-[58px] text-[#0D2A4C] tracking-tighter font-semibold">
                    Build Ready-to-Use Lead Lists in Minutes
                  </p>
                  <div className="flex gap-2">
                    <Link
                      href="/pricing"
                      className="bg-[#2A8DFF] w-fit text-center hover:bg-[#2A8DFF]/90 active:outline-1 active:outline-[#2A8DFF] active:bg-[#2A8DFF] rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-white text-sm md:text-[16px] font-semibold">
                        Get Started Now
                      </p>
                    </Link>
                    <Link
                      href="/demo-form"
                      className="bg-white w-fit hover:bg-[#2A8DFF]/5 active:outline-1 active:!outline-white active:bg-white rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-[#0D2A4C] text-sm md:text-[16px] font-semibold">
                        Book a Demo
                      </p>
                    </Link>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex gap-1 items-center">
                    <ListChecksIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Access 710M+ verified contacts
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Clock2Icon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Export in under 10 minutes
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <UserPenIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Find leads by job title, size, tech stack
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <FileX2Icon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      No spreadsheets or scraping needed
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:block w-full px-5">
                <Image
                  src="/images/list-tab.webp"
                  alt="List Tab"
                  width={2140}
                  height={706}
                  className="w-full"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
              <div className="block md:hidden">
                <Image
                  src="/images/list-tab-mobile.webp"
                  alt="List Tab"
                  width={780}
                  height={632}
                  className="w-full"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
            </TabsContent>
            <TabsContent
              value="crm"
              className="w-full gap-[42px] md:gap-16 flex flex-col overflow-hidden bg-[#F8F9FA] border border-[#0D2A4C08] lg:px-28 pt-8 md:pt-12"
            >
              <div className="flex flex-col md:flex-row md:justify-between gap-8 px-5">
                <div className="flex flex-col gap-3.5 md:gap-5">
                  <p className="max-w-[297px] md:max-w-[548px] text-[32px] md:text-[56px] leading-[32px] md:leading-[58px] text-[#0D2A4C] tracking-tighter font-semibold">
                    Send Leads Straight to Your Sales Tools
                  </p>
                  <div className="flex gap-2">
                    <Link
                      href="/pricing"
                      className="bg-[#2A8DFF] w-fit text-center hover:bg-[#2A8DFF]/90 active:outline-1 active:outline-[#2A8DFF] active:bg-[#2A8DFF] rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-white text-sm md:text-[16px] font-semibold">
                        Get Started Now
                      </p>
                    </Link>
                    <Link
                      href="/demo-form"
                      className="bg-white w-fit hover:bg-[#2A8DFF]/5 active:outline-1 active:!outline-white active:bg-white rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-[#0D2A4C] text-sm md:text-[16px] font-semibold">
                        Book a Demo
                      </p>
                    </Link>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex gap-1 items-center">
                    <RefreshCwIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Sync with Instantly, HubSpot, Smartlead
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <SendIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Send leads directly to your CRM
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <ClipboardXIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Eliminate manual uploads
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <MailCheckIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Automate outreach instantly
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:block w-full px-5">
                <Image
                  src="/images/crm-tab.webp"
                  alt="CRM Tab"
                  width={2140}
                  height={746}
                  className="w-full"
                  sizes="(min-width: 768px) 100vw"
                />
              </div>
              <div className="block md:hidden">
                <Image
                  src="/images/crm-tab-mobile.webp"
                  alt="CRM Tab"
                  width={780}
                  height={626}
                  className="w-full"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
            </TabsContent>
            <TabsContent
              value="cold"
              className="w-full gap-[42px] md:gap-16 flex flex-col overflow-hidden bg-[#F8F9FA] border border-[#0D2A4C08] lg:px-28 pt-8 md:pt-12"
            >
              <div className="flex flex-col md:flex-row md:justify-between gap-8 px-5">
                <div className="flex flex-col gap-3.5 md:gap-5">
                  <p className="max-w-[297px] md:max-w-[548px] text-[32px] md:text-[56px] leading-[32px] md:leading-[58px] text-[#0D2A4C] tracking-tighter font-semibold">
                    Cold Email Training From The #1 Experts
                  </p>
                  <div className="flex gap-2">
                    <Link
                      href="/pricing"
                      className="bg-[#2A8DFF] w-fit text-center hover:bg-[#2A8DFF]/90 active:outline-1 active:outline-[#2A8DFF] active:bg-[#2A8DFF] rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-white text-sm md:text-[16px] font-semibold">
                        Get Started Now
                      </p>
                    </Link>
                    <Link
                      href="/demo-form"
                      className="bg-white w-fit hover:bg-[#2A8DFF]/5 active:outline-1 active:!outline-white active:bg-white rounded-[100px] py-[11px] px-[18px] md:py-[15px] md:px-[22px] cursor-pointer"
                    >
                      <p className="text-[#0D2A4C] text-sm md:text-[16px] font-semibold">
                        Book a Demo
                      </p>
                    </Link>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:gap-4">
                  <div className="flex gap-1 items-center">
                    <TrophyIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Training trusted by 2,000+ teams
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <CircleDollarSignIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Includes $497 Cold Email Mastery
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <PhoneOutgoingIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Book calls faster with proven scripts
                    </p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <MessageSquareIcon className="size-4 md:size-5 text-[#0D2A4C99]" />
                    <p className="text-sm leading-[22px] md:text-[16px] md:leading-6 text-[#0D2A4C99]">
                      Get 1-on-1 strategy calls at signup
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:block w-full px-5">
                <Image
                  src="/images/cold-tab.webp"
                  alt="Cold Tab"
                  width={2140}
                  height={750}
                  className="w-full"
                  sizes="(min-width: 768px) 100vw"
                />
              </div>
              <div className="block md:hidden">
                <Image
                  src="/images/cold-tab-mobile.webp"
                  alt="Cold Tab"
                  width={400}
                  height={300}
                  className="w-full"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}
